import { AESCipherSIVNonce96 } from '../../base/cipher/aes';
import { AbstractSelfAccountKeyring } from '../../keyring/account_base';
import { PeerAccountKeyring } from '../../keyring/account_peer';
import { AbstractCryptoConfig, CryptoConfigVersioning } from '../base';
import { AccountPairDeriveKeyV2, KemSharedSecretSelectorV1, TS_WINDOW_V1 } from '../common';
import { DialogueConfigDumpArgs, DialogueConfigLoadArgs } from './args';
import { makeInfoByteArray } from '../../utility';
import { CryptoUpgrade } from '../feature';

/**
 * Dialogue message encryption: AES-SIV
 *         KEY: 32B from curve25519 scalar_mult + 32B from MLKEM1024 (ss-id encoded in version by acc-id)
 *             -> 64B HKDF by context + current time-slice-window
 *         NONCE: 12B random
 *
 *     Key rotation by time is not necessary here by the key usage safety limits, but we do it nevertheless because a
 *     timestamp is always available and we can limit the blast radius of a single key leak (even if not really relevant).
 *     NOTE: Large LRU is useful here for pair and given time-window.
 */
class _DialogueConfigV2V3 extends AbstractCryptoConfig<
  DialogueConfigLoadArgs,
  DialogueConfigDumpArgs
> {
  public other_version: Uint8Array;
  public static TS_WINDOW = TS_WINDOW_V1;

  constructor(version: number, other_version: number, cu: CryptoUpgrade) {
    const other_config_version = CryptoConfigVersioning.config_version_from_int(other_version);
    const config_version = CryptoConfigVersioning.config_version_from_int(version);
    if (other_config_version === null || config_version === null) {
      throw new Error('Invalid version number for crypto config');
    }
    super(version, cu, [config_version, other_config_version]);
    this.other_version = other_config_version;
  }

  public load(args: DialogueConfigLoadArgs): Promise<Uint8Array> {
    return new Promise((resolve, reject) => {
      let error;

      let cipher = args.cipher.slice(this.version.byteLength);
      let timestampArr = _DialogueConfigV2V3.TS_WINDOW.windows(args.timestamp);

      let _load = (timestamp_index) => {
        if (timestamp_index >= timestampArr.length) {
          reject(error);
          return;
        }

        let timestamp_window = timestampArr[timestamp_index];

        this.derive_key(
          KemSharedSecretSelectorV1.select_ss_for_load(
            args.self_kr,
            args.peer_kr,
            this.version,
            this.other_version
          ),
          this.version,
          args.self_kr,
          args.peer_kr,
          timestamp_window
        )
          .then((derive_key) => {
            AESCipherSIVNonce96.decrypt(cipher, derive_key)
              .then((res) => {
                resolve(res);
                return;
              })
              .catch((err) => {
                error = err;
                _load(timestamp_index + 1);
              });
          })
          .catch(reject);
      };

      _load(0);
    });
  }

  public dump(args: DialogueConfigDumpArgs): Promise<Uint8Array> {
    const ssv = KemSharedSecretSelectorV1.select_ss_version_for_dump(
      args.self_kr,
      args.peer_kr,
      this.version,
      this.other_version
    );
    return this.derive_key(
      ssv.shared_secret,
      ssv.selected_version,
      args.self_kr,
      args.peer_kr,
      _DialogueConfigV2V3.TS_WINDOW.window(args.timestamp)
    ).then((derive_key) => {
      return AESCipherSIVNonce96.encrypt(
        args.plain,
        derive_key,
        undefined,
        undefined,
        ssv.selected_version
      );
    });
  }

  public derive_key(
    shared_secret: Uint8Array,
    version: Uint8Array,
    self_kr: AbstractSelfAccountKeyring,
    peer_kr: PeerAccountKeyring,
    ts_window: Uint8Array
  ) {
    return AccountPairDeriveKeyV2.derive_key(
      shared_secret,
      self_kr,
      peer_kr,
      makeInfoByteArray(['dialogue-', version, '-', ts_window])
    );
  }
}

export class DialogueConfigV2 extends _DialogueConfigV2V3 {
  constructor(cu: CryptoUpgrade) {
    super(2, 3, cu);
  }
}

export class DialogueConfigV3 extends _DialogueConfigV2V3 {
  constructor(cu: CryptoUpgrade) {
    super(3, 2, cu);
  }
}
